import React, {FC, ReactNode, RefObject, useLayoutEffect, useRef} from 'react';
import styled from 'styled-components';

import chatBotAvatarSvg from '../../../../../libs/shared/assets/src/images/chatbotAvatar.svg';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {Layer} from '../../../../../libs/shared/ui/src/components/layers/layer';
import {shadows} from '../../../../../libs/shared/ui/src/styles/colorStyles';
import {fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {LightUserThemeProvider} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {SandboxScenariosEnum, StepSlug} from '../../../../helpers/sandbox/sandboxScenarioHelpers';

/*
 * Props.
 */

interface SandboxChatPreviewProps {
  windowRef: RefObject<HTMLDivElement> | undefined;
  articleRef: RefObject<HTMLDivElement> | undefined;
  step: string | undefined;
}

/*
 * Style.
 */

const StyledWindowDiv = styled.div`
  position: absolute;
  right: 20px;
  bottom: 20px;
  width: 384px;
  height: 640px;
  max-height: 80%;

  border-radius: 8px;
  background-color: ${(p) => p.theme.foreground};
  color: ${(p) => p.theme.text};
  ${shadows.dialogsAndDropdowns};

  display: flex;
  flex-direction: column;
`;

const StyledTitleDiv = styled.div`
  flex: none;
  padding: 6px;

  font-size: 13px;
  font-weight: ${fontWeights.bold};
  line-height: 16px;
  text-align: center;
`;

const StyledHeaderDiv = styled.div`
  flex: none;
  padding: 18px 16px;
  background-color: ${(p) => p.theme.brand};
  color: ${(p) => p.theme.greys.white};

  h1 {
    font-size: 16px;
    font-weight: ${fontWeights.bold};
    line-height: 19px;
    margin-top: 0;
    margin-bottom: 4px;
  }

  p {
    font-size: 12px;
    line-height: 14px;
    margin: 0;
  }
`;

const StyledMessageListDiv = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0 16px;
`;

const agentAvatarWidth = 30;
const agentAvatarHorizontalGap = 4;

const StyledAgentNameDiv = styled.div`
  font-size: 12px;
  line-height: 14px;
  color: ${(p) => p.theme.greys.shade60};

  margin-top: 16px;
  margin-bottom: 8px;
  margin-left: ${agentAvatarWidth + agentAvatarHorizontalGap + 12}px;
`;

const StyledAgentFirstMessageDiv = styled.div`
  margin-top: 4px;
  margin-bottom: 4px;
  margin-right: 78px;

  display: flex;
  gap: 4px;
  align-items: flex-start;
`;

const StyledAgentAvatarImg = styled.img`
  flex: none;
  width: 30px;
  height: 30px;
`;

const StyledAgentFirstBubbleDiv = styled.div`
  background-color: ${(p) => p.theme.greys.shade20};
  border-radius: 18px 18px 18px 4px;
  padding: 8px 12px;

  font-size: 14px;
  line-height: 20px;
`;

const StyledAgentLastMessageDiv = styled.div`
  margin-top: 4px;
  margin-bottom: 4px;
  margin-left: ${agentAvatarWidth + agentAvatarHorizontalGap}px;
  margin-right: 78px;

  display: flex;
`;

const StyledAgentLastBubbleDiv = styled.div`
  background-color: ${(p) => p.theme.greys.shade20};
  border-radius: 4px 18px 18px 18px;
  padding: 8px 12px;

  font-size: 14px;
  line-height: 20px;
`;

const StyledAgentFooterDiv = styled.div`
  font-size: 12px;
  line-height: 14px;
  color: ${(p) => p.theme.greys.shade60};

  margin-top: 8px;
  margin-bottom: 16px;
  margin-left: ${agentAvatarWidth + agentAvatarHorizontalGap + 12}px;
`;

const StyledVisitorFirstMessageDiv = styled.div`
  margin-top: 4px;
  margin-bottom: 4px;
  margin-left: 112px;

  display: flex;
  justify-content: flex-end;
`;

const StyledVisitorFirstBubbleDiv = styled.div`
  background-color: ${(p) => p.theme.brand};
  border-radius: 18px 18px 4px 18px;
  padding: 8px 12px;

  color: ${(p) => p.theme.greys.white};
  font-size: 14px;
  line-height: 20px;
`;

const StyledVisitorFooterDiv = styled.div`
  font-size: 12px;
  line-height: 14px;
  color: ${(p) => p.theme.greys.shade60};
  text-align: right;

  margin-top: 8px;
  margin-bottom: 16px;
`;

const StyledKnowledgeBaseArticleListDiv = styled.div`
  margin-top: 9px;
  margin-bottom: 9px;
  margin-left: ${agentAvatarWidth + agentAvatarHorizontalGap}px;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const StyledKnowledgeBaseArticleDiv = styled.div`
  padding: 8px;
  outline: 2px solid ${(p) => p.theme.greys.shade20};
  border-radius: 16px;

  display: flex;
  gap: 8px;
`;

const StyledKnowledgeBaseArticleIconDiv = styled.div`
  padding: 19px 13px;
  border-radius: 8px;
  background-color: ${(p) => p.theme.greys.shade20};
`;

const StyledKnowledgeBaseArticleContentDiv = styled.div`
  white-space: nowrap;

  h1 {
    font-size: 14px;
    font-weight: ${fontWeights.bold};
    line-height: 20px;
    margin: 0;
    margin-bottom: 2px;
  }

  p {
    color: ${(p) => p.theme.greys.shade70};
    font-size: 12px;
    font-weight: ${fontWeights.semibold};
    line-height: 18px;
    margin: 0;
  }
`;

/*
 * Component.
 */

export const SandboxChatPreview: FC<SandboxChatPreviewProps> = (props) => (
  <Layer>
    <LightUserThemeProvider>
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <ChatWindow {...props} />
    </LightUserThemeProvider>
  </Layer>
);

const ChatWindow: FC<SandboxChatPreviewProps> = (props) => {
  const messageListRef = useRef<HTMLDivElement>(null);
  const billingMessageRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const messageListElement = messageListRef.current;
    if (!messageListElement) {
      return;
    }

    switch (props.step) {
      // For the first tip, scroll to the bottom.
      case 'start-scenario-3-conversation-4' satisfies StepSlug<SandboxScenariosEnum.SCENARIO3_ACT4>:
        messageListElement.scrollTo({
          top: messageListElement.scrollHeight - messageListElement.getBoundingClientRect().height,
          behavior: 'instant',
        });
        break;

      // For the second tip, scroll to the billing message.
      case 'showcase-knowledge-base' satisfies StepSlug<SandboxScenariosEnum.SCENARIO3_ACT4>: {
        const billingMessageElement = billingMessageRef.current;
        if (!billingMessageElement) {
          break;
        }

        const messageListBoundingTop = messageListElement.getBoundingClientRect().top;
        const messageListContentBoundingTop = messageListBoundingTop - messageListElement.scrollTop;
        messageListElement.scrollTo({
          top: billingMessageElement.getBoundingClientRect().top - messageListContentBoundingTop - 8,
          behavior: 'smooth',
        });
        break;
      }
      default:
    }
  }, [props.step]);

  return (
    <StyledWindowDiv ref={props.windowRef}>
      <StyledTitleDiv>
        <div>Chat preview</div>
      </StyledTitleDiv>

      <StyledHeaderDiv>
        <h1>Welcome</h1>
        <p>How can we help?</p>
      </StyledHeaderDiv>

      <StyledMessageListDiv ref={messageListRef}>
        <AgentFirstMessage>Hi there, how can we help?</AgentFirstMessage>
        <StyledAgentFooterDiv>2m</StyledAgentFooterDiv>

        <VisitorFirstMessage messageRef={billingMessageRef}>
          How do I update my billing address?
        </VisitorFirstMessage>
        <StyledVisitorFooterDiv>Sent - 2m</StyledVisitorFooterDiv>

        <AgentFirstMessage>This article may be helpful</AgentFirstMessage>
        <KnowledgeBaseArticleList articleRef={props.articleRef} />
        <AgentLastMessage>Did that answer your question?</AgentLastMessage>
        <StyledAgentFooterDiv>2m</StyledAgentFooterDiv>

        <VisitorFirstMessage>Yes</VisitorFirstMessage>
        <StyledVisitorFooterDiv>Sent - 2m</StyledVisitorFooterDiv>

        <AgentFirstMessage>Is there anything else I can help you with?</AgentFirstMessage>
        <StyledAgentFooterDiv>1m</StyledAgentFooterDiv>

        <VisitorFirstMessage>
          I need to change the delivery date for this month&apos;s order
        </VisitorFirstMessage>
        <StyledVisitorFooterDiv>Sent - 1m</StyledVisitorFooterDiv>

        <AgentFirstMessage>
          Hmm... I&apos;ll need to get someone to help with that. A member of our team will follow up with you
          in just a minute!
        </AgentFirstMessage>
        <StyledAgentFooterDiv>1m</StyledAgentFooterDiv>
      </StyledMessageListDiv>
    </StyledWindowDiv>
  );
};

const AgentFirstMessage: FC<{children: ReactNode}> = (props) => (
  <>
    <StyledAgentNameDiv>Your fictional chatbot</StyledAgentNameDiv>
    <StyledAgentFirstMessageDiv>
      <StyledAgentAvatarImg src={chatBotAvatarSvg} />
      <StyledAgentFirstBubbleDiv>{props.children}</StyledAgentFirstBubbleDiv>
    </StyledAgentFirstMessageDiv>
  </>
);

const AgentLastMessage: FC<{children: ReactNode}> = (props) => (
  <StyledAgentLastMessageDiv>
    <StyledAgentLastBubbleDiv>{props.children}</StyledAgentLastBubbleDiv>
  </StyledAgentLastMessageDiv>
);

const VisitorFirstMessage: FC<{messageRef?: RefObject<HTMLDivElement>; children: ReactNode}> = (props) => (
  <StyledVisitorFirstMessageDiv ref={props.messageRef}>
    <StyledVisitorFirstBubbleDiv>{props.children}</StyledVisitorFirstBubbleDiv>
  </StyledVisitorFirstMessageDiv>
);

const KnowledgeBaseArticleList: FC<{articleRef: RefObject<HTMLDivElement> | undefined}> = (props) => (
  <StyledKnowledgeBaseArticleListDiv>
    <StyledKnowledgeBaseArticleDiv ref={props.articleRef}>
      <StyledKnowledgeBaseArticleIconDiv>
        <Icon name="knowledgeBase" />
      </StyledKnowledgeBaseArticleIconDiv>
      <StyledKnowledgeBaseArticleContentDiv>
        <h1>Update account settings</h1>
        <p>
          Account is where you store your
          <br /> name, contact information, and&hellip;
        </p>
      </StyledKnowledgeBaseArticleContentDiv>
    </StyledKnowledgeBaseArticleDiv>
  </StyledKnowledgeBaseArticleListDiv>
);
